import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Image as ImageIcon, Video, Tag } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import * as educationService from '../services/educationService';
import type { EducationCategory, CreateArticleData } from '../services/educationService';

export default function AdminArticleEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id && id !== 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<EducationCategory[]>([]);
  
  const [form, setForm] = useState<{
    categoryId: string;
    type: 'ARTICLE' | 'VIDEO' | 'FAQ';
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    imageUrl: string;
    videoUrl: string;
    tags: string[];
    relatedDiseases: string[];
    order: number;
    authorName: string;
    authorTitle: string;
    readTime: number;
    isPublished: boolean;
    isFeatured: boolean;
  }>({
    categoryId: '',
    type: 'ARTICLE',
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    imageUrl: '',
    videoUrl: '',
    tags: [] as string[],
    relatedDiseases: [] as string[],
    order: 0,
    authorName: '',
    authorTitle: '',
    readTime: 0,
    isPublished: false,
    isFeatured: false
  });

  const [tagInput, setTagInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadCategories();
    if (isEditMode) {
      loadArticle();
    }
  }, [id]);

  const loadCategories = async () => {
    try {
      const data = await educationService.getAdminCategories();
      setCategories(data.filter(c => c.isActive));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadArticle = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const article = await educationService.getAdminArticle(id);
      setForm({
        categoryId: article.categoryId,
        type: article.type,
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt || '',
        content: article.content,
        imageUrl: article.imageUrl || '',
        videoUrl: article.videoUrl || '',
        tags: article.tags,
        relatedDiseases: article.relatedDiseases,
        order: article.order,
        authorName: article.authorName || '',
        authorTitle: article.authorTitle || '',
        readTime: article.readTime || 0,
        isPublished: article.isPublished,
        isFeatured: article.isFeatured
      });
    } catch (error) {
      alert('Eroare la încărcarea articolului');
      navigate('/admin/education');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.trim().split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const handleChange = (field: string, value: any) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate slug when title changes
      if (field === 'title' && !isEditMode) {
        updated.slug = generateSlug(value);
      }
      
      // Auto-calculate read time when content changes
      if (field === 'content') {
        updated.readTime = estimateReadTime(value);
      }
      
      return updated;
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Te rog selectează o imagine validă');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Imaginea este prea mare. Maxim 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await educationService.uploadImage(file);
      setForm(prev => ({ ...prev, imageUrl: result.imageUrl }));
      alert('✅ Imagine încărcată cu succes!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la încărcarea imaginii');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async (publish: boolean = false) => {
    // Validation
    if (!form.title.trim()) {
      alert('Titlul este obligatoriu!');
      return;
    }
    if (!form.slug.trim()) {
      alert('Slug-ul este obligatoriu!');
      return;
    }
    if (!form.categoryId) {
      alert('Categoria este obligatorie!');
      return;
    }
    if (!form.content.trim()) {
      alert('Conținutul este obligatoriu!');
      return;
    }

    setSaving(true);
    try {
      const data: CreateArticleData = {
        ...form,
        isPublished: publish,
        videoUrl: form.videoUrl || undefined
      };

      if (isEditMode && id) {
        await educationService.updateArticle(id, data);
        alert('✅ Articol actualizat!');
      } else {
        await educationService.createArticle(data);
        alert('✅ Articol creat!');
      }
      
      navigate('/admin/education');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <PageHeader 
        title={isEditMode ? 'Editează Articol' : 'Articol Nou'}
        subtitle="Education Module"
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/admin/education')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Înapoi la listă
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Salvează Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <Eye className="w-4 h-4" />
              {saving ? 'Se salvează...' : 'Publică'}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titlu <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Introdu titlul articolului..."
                className="w-full px-4 py-3 text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Slug */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug (URL) <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">/educatie/articole/</span>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  placeholder="slug-articol"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extras (Preview)
              </label>
              <textarea
                value={form.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                placeholder="Un scurt rezumat al articolului care va apărea în lista de articole..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Content - Rich Text Area (Simple for now) */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conținut <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Scrie conținutul articolului aici... (Suport pentru Markdown)"
                rows={20}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-2">
                Timp estimat de citire: <strong>{form.readTime} min</strong> | Cuvinte: <strong>{form.content.trim().split(/\s+/).length}</strong>
              </p>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                Tag-uri
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Adaugă tag și apasă Enter..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Adaugă
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Category */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categorie <span className="text-red-500">*</span>
              </label>
              <select
                value={form.categoryId}
                onChange={(e) => handleChange('categoryId', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Selectează categoria</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tip Articol
              </label>
              <select
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="ARTICLE">📝 Articol</option>
                <option value="VIDEO">🎥 Video</option>
              </select>
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ImageIcon className="w-4 h-4 inline mr-1" />
                Imagine Featured
              </label>
              
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 disabled:opacity-50"
              />
              
              {uploadingImage && (
                <div className="mt-3 flex items-center gap-2 text-sm text-purple-600">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Se încarcă imaginea...
                </div>
              )}
              
              {form.imageUrl && !uploadingImage && (
                <div className="mt-3">
                  <img
                    src={form.imageUrl}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <button
                    onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}
                    className="mt-2 text-xs text-red-600 hover:text-red-800"
                  >
                    Șterge imagine
                  </button>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-2">
                JPEG, PNG, WebP max 5MB
              </p>
            </div>

            {/* Video URL (if type is VIDEO) */}
            {form.type === 'VIDEO' && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Video className="w-4 h-4 inline mr-1" />
                  URL Video (YouTube)
                </label>
                <input
                  type="url"
                  value={form.videoUrl}
                  onChange={(e) => handleChange('videoUrl', e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Author */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Autor
              </label>
              <input
                type="text"
                value={form.authorName}
                onChange={(e) => handleChange('authorName', e.target.value)}
                placeholder="Nume autor"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-2"
              />
              <input
                type="text"
                value={form.authorTitle}
                onChange={(e) => handleChange('authorTitle', e.target.value)}
                placeholder="Titlu autor (ex: Nutriționist)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Options */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Articol Featured</span>
                <button
                  type="button"
                  onClick={() => handleChange('isFeatured', !form.isFeatured)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.isFeatured ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.isFeatured ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Ordine</span>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
                  className="w-20 px-3 py-1 border border-gray-300 rounded-lg text-center"
                  min={0}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
